import PropTypes from 'prop-types';
import React, { PureComponent, createRef, Fragment } from 'react';
import styled from 'styled-components';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import { connect } from 'react-redux';

import {
  Dropdown,
  Button,
  Icon,
  Menu,
  Modal,
  Header,
  Form,
  TextArea,
  Checkbox,
  Divider,
} from 'semantic-ui-react';
import { Button as UploadButton, Row, Text } from '@cuemath/comps';
import { FileUpload } from 'styled-icons/fa-solid/FileUpload';
import { Close } from 'styled-icons/material/Close';
import {
  getAll as getChapterList,
  post as postChapter,
  patch as patchChapter,
} from 'actions/chapters';
import { Message } from 'modules/form';
import { getAll as getDraftList } from 'actions/drafts';
import NotificationPortal from 'comps/notification-portal';
import { handleAWSS3Upload, getAWSSignedKeyChapters } from 'modals/utils';

/*
Define dropdown with incoming values, default incoming value, plus '+' button for adding new entity given it's structure, default values, 'name' field and post request action.
On plus button, modal should be allowed to show with structure as sent to this component.
When the post request is complete, this dropdown's value should equal that of the new 'name' and 'key'.
*/

const DEFAULTCHAPTERDATA = {
  is_live: false,
  description: '',
};

const GET_DRAFT_PARAMS = {
  is_materialized: false,
};

const imageFormats = ['jpg', 'png', 'svg'];

const DeleteIcon = styled(Close)`
  cursor: pointer;
  color: red;
  width: 24px;
  height: 24px;
  margin-left: 20px;
  margin-top: 10px;
`;

const HeaderButton = styled(UploadButton)`
  margin-left: 10px;
`;

const RowStyle = styled(Row)`
  margin-top: 10px;
`;

const TextStyle = styled(Text)`
  margin-left: 20px;
  margin-top: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 9em;
`;

let editedRef;

class ChapterDropdown extends PureComponent {
  static propTypes = {
    chapters: PropTypes.shape({}),
    drafts: PropTypes.shape({}),
    actions: PropTypes.shape({}).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeChapterCode: null,
      activeEntityID: null,
      chapterPostObj: store.post(),
      chapterPatchObj: store.patch(),
      error: null,
      file: null,
      fileExt: '',
      modalOpen: false,
      chapterData: {},
      openSavedNotificationPortal: false,
      notificationMessage: '',
      isNotificationError: undefined,
    };

    this.inputFile = createRef(null);

    this.sendChapterData = this.sendChapterData.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { program, actions } = this.props;

    if (program && program !== prevProps.program) {
      actions.getChapterList(program.id);
    }
  }

  static getDerivedStateFromProps({ chapters, actions }, prevState) {
    let newState = {};
    if (chapters) {
      const chaptersPost = store.post(chapters, prevState.chapterPostObj.id);
      if (chaptersPost.resolved && chaptersPost.resolved.isCreated) {
        newState['chapterPostObj'] = chaptersPost;
        newState['chapterData'] = {};
        newState['openSavedNotificationPortal'] = true;
        newState['notificationMessage'] = chaptersPost.resolved.data.name + ' saved';
      } else if (chaptersPost.resolved && chaptersPost.resolved.isPatchingFailed) {
        const error = store.getError(chaptersPost.resolved);
        newState['chaptersPostObj'] = chaptersPost;
        newState['openSavedNotificationPortal'] = true;
        newState['notificationMessage'] =
          '\n Chapter Not Editable due to error: ' + error.message;
        newState['isNotificationError'] = true;
      }

      const chapterPatch = store.patch(chapters, prevState.chapterPatchObj.id);
      if (chapterPatch.resolved && chapterPatch.resolved.isPatched) {
        newState['chapterPatchObj'] = chapterPatch;
        newState['chapterData'] = {};
        newState['openSavedNotificationPortal'] = true;
        newState['notificationMessage'] = chapterPatch.resolved.data.name + ' saved';
      } else if (chapterPatch.resolved && chapterPatch.resolved.isPatchingFailed) {
        const error = store.getError(chapterPatch.resolved);
        newState['chapterPatchObj'] = chapterPatch;
        newState['openSavedNotificationPortal'] = true;
        newState['notificationMessage'] =
          '\n Chapter Not Editable due to error: ' + error && error.message
            ? error.message
            : '';
        newState['isNotificationError'] = true;
      }
      return newState;
    }
    return null;
  }

  getAWSKeyOnEdit = fileType => {
    //appending timestamp to png and json urls each time an activity is edited, because browser caches these urls
    const { awsSignedKey, chapterData, fileExt } = this.state;

    const { code, version } = chapterData || {};

    const { bucketName } = awsSignedKey || {};

    if (fileType === 'application/json') {
      const url = `https://${bucketName}.s3.amazonaws.com/`;
      const key = `media/chapter-images/${code}_${!version ? 1 : version}.${fileExt}`;

      editedRef = `${url}${key}`;

      return `media/chapter-images/${code}_${!version ? 1 : version}.${fileExt}`;
    }
    return `media/chapter-images/${code}_${!version ? 1 : version}.${fileExt}`;
  };

  handleFileUploadToS3 = (file, resolve, reject) => {
    const { awsSignedKey } = this.state;

    const { type } = file || {};
    const key = this.getAWSKeyOnEdit(type);

    handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
  };

  changeActiveDropdownContent = (event, { value }) => {
    const { chapters, program } = this.props;
    let newState = {};
    newState['activeEntityID'] = value;
    if (program) {
      const chapterList = store.getAll(chapters, program.id);
      const toggleEditChapter = chapterList.data.find(chp => chp.id === value)
        ? true
        : false;
      newState['isChapter'] = toggleEditChapter;
    }
    this.setState({
      ...newState,
    });
  };

  changeChapterCodeDropdownContent = (event, { value }) => {
    const { setChapterCode, chapters, program } = this.props;
    const { getDraftList } = this.props.actions;
    this.setState(
      {
        activeChapterCode: value,
      },
      () => {
        const queryParams = { ...GET_DRAFT_PARAMS, chapter_code: value };
        const chapterList = store.getAll(chapters, program.id);
        let chpData = chapterList.data.find(chp => chp.code === value);
        setChapterCode(chpData);
        getDraftList(queryParams);
      },
    );
  };

  sendChapterData(code) {
    const { setChapter, chapters, program, drafts } = this.props;
    const { activeEntityID, activeChapterCode } = this.state;
    const chapterList = store.getAll(chapters, program.id);
    const queryParams = { ...GET_DRAFT_PARAMS, chapter_code: activeChapterCode };
    const draftList = store.getAll(drafts, queryParams);
    const chpData = chapterList.data.find(chp => chp.id === activeEntityID);
    let draftData;
    if (!chpData) {
      draftData = draftList.data.find(draft => draft.id === activeEntityID);
      setChapter(draftData, false);
    } else {
      setChapter(chpData, true);
    }
  }

  getChapterVersionOptions() {
    const { activeChapterCode } = this.state;
    const { drafts, chapters, program } = this.props;
    if (program) {
      const chapterList = store.getAll(chapters, program.id);
      let draftList;
      if (activeChapterCode) {
        const queryParams = { ...GET_DRAFT_PARAMS, chapter_code: activeChapterCode };
        draftList = store.getAll(drafts, queryParams);
      }
      let chapterVersionOptions = [];
      if (chapterList) {
        const chapterVersions = chapterList.data.filter(
          chapter => chapter.code === activeChapterCode,
        );
        chapterVersions.forEach(chapter => {
          chapterVersionOptions.push({
            key: chapter.id,
            value: chapter.id,
            text: chapter.version,
            label: { color: 'green', empty: true, circular: true },
          });
        });
      }
      if (draftList && draftList.data) {
        draftList.data.forEach(draft => {
          chapterVersionOptions.push({
            key: draft.id,
            value: draft.id,
            text: draft.name,
            label: { color: 'red', empty: true, circular: true },
          });
        });
      }

      return chapterVersionOptions;
    }
    return [];
  }

  getChapterOptions() {
    const { chapters, program } = this.props;
    if (chapters && program) {
      const chapterList = store.getAll(chapters, this.props.program.id);
      if (chapterList) {
        let chapterNameOptions = {};
        const chapterCodes = [
          ...new Set(
            chapterList.data.map(x => {
              chapterNameOptions[x.code] = x.name;
              return x.code;
            }),
          ),
        ];
        const chapterCodeOptions = chapterCodes.map(chapterCode => ({
          key: chapterCode,
          text: chapterNameOptions[chapterCode],
          value: chapterCode,
        }));

        return chapterCodeOptions;
      }
    }
    return [];
  }

  editChapterFields = () => {
    const { patchChapter } = this.props.actions;
    const { chapterData, chapterPatchObj, file } = this.state;

    if (chapterData['']) delete chapterData[''];

    if (!file) chapterData['image_url'] = null;

    let newChapterData = Object.assign({}, chapterData);
    newChapterData['position'] = parseInt(chapterData['position']);
    patchChapter(chapterPatchObj.id, newChapterData.id, newChapterData);
  };

  saveChapter = () => {
    const { program } = this.props;
    const { postChapter } = this.props.actions;
    const { chapterData, chapterPostObj, isChapterNew } = this.state;

    if (chapterData['']) delete chapterData[''];

    let newChapterData = Object.assign({}, chapterData);
    newChapterData['position'] = parseInt(chapterData['position']);
    if (isChapterNew) {
      postChapter(chapterPostObj.id, newChapterData, { programID: program.id });
    }
  };

  handleChange = e => {
    let tempData = Object.assign({}, this.state.chapterData);
    tempData[e.target.name] = e.target.value;
    this.setState({
      chapterData: tempData,
    });
  };

  handleChangeCheckbox = (e, { checked, name }) => {
    let tempData = Object.assign({}, this.state.chapterData);
    tempData[name] = checked;
    this.setState({
      chapterData: tempData,
    });
  };

  handleModalClose = () => {
    this.setState({
      modalOpen: false,
    });
  };

  openModal = (data, isNew) => {
    this.setState({
      modalOpen: true,
      chapterData: data,
      file: data && data.image_url,
      isChapterNew: isNew,
    });
  };

  newChapter = () => {
    const { program, chapters } = this.props;
    const chapterList = store.getAll(chapters, program.id);
    let chpData = Object.assign({}, DEFAULTCHAPTERDATA);
    chpData['program_id'] = program.id;
    if (chapterList.data.length > 0) {
      chpData['position'] =
        Math.max.apply(
          Math,
          chapterList.data.map(function(o) {
            return o.position;
          }),
        ) + 1;
    } else {
      chpData['position'] = 1;
    }
    this.openModal(chpData, true);
  };

  viewChapter = () => {
    const { chapters, program } = this.props;
    const chapterList = store.getAll(chapters, program.id);
    const { activeEntityID } = this.state;
    let chpData = chapterList.data.find(chp => chp.id === activeEntityID);
    this.openModal(chpData, false);
  };

  handleCloseNotificationPortal = () => {
    this.setState({
      openSavedNotificationPortal: false,
      notificationMessage: '',
    });
  };

  handleSubmit = async () => {
    const { chapterData, file, isChapterNew } = this.state;

    this.handleModalClose();

    if (
      (file && chapterData && !chapterData.image_url) ||
      (file && chapterData && chapterData.image_url && chapterData.image_url !== file)
    ) {
      await this.handleS3Upload();
    }

    if (isChapterNew) {
      this.saveChapter();
    } else {
      this.editChapterFields();
    }
  };

  handleS3Upload = () => {
    const { file } = this.state;

    return new Promise((resolve, reject) => {
      this.handleFileUploadToS3(file, resolve, reject);
    });
  };

  onButtonClick = () => {
    this.inputFile.current.click();
  };

  getAwsSignedKey = async () => {
    const { awsSignedKey } = this.state;

    if (!awsSignedKey || (awsSignedKey && awsSignedKey.isStale)) {
      const awsSignedKey = await getAWSSignedKeyChapters();

      this.setState({
        awsSignedKey,
      });
    }
  };

  setImage = e => {
    e.preventDefault();

    let file = e.target.files[0];
    const { chapterData } = this.state;

    const data = chapterData;

    const { name } = file || {};

    const { code, version } = chapterData || {};

    const ext = name && name.split('.').pop();

    const isCorrectFormat = imageFormats.includes(ext);

    if (!isCorrectFormat) {
      return this.setState({
        error: 'Please upload the images in png, jpg, or svg format only.',
      });
    } else {
      data['image_url'] = `${code}_${!version ? 1 : version}.${ext}`;

      this.setState({
        file: file,
        chapterData: data,
        fileExt: ext,
        error: null,
      });

      this.getAwsSignedKey();
    }
  };

  deleteImage = e => {
    e.preventDefault();

    const { chapterData } = this.state;

    const data = chapterData;

    delete data['image_url'];

    this.setState({
      file: null,
      chapterData: { ...chapterData, ...data },
    });
  };

  render() {
    const { program } = this.props;
    const {
      activeChapterCode,
      activeEntityID,
      error,
      file,
      modalOpen,
      chapterData,
      isChapterNew,
      openSavedNotificationPortal,
      notificationMessage,
      isNotificationError,
      isChapter,
    } = this.state;

    const { name: fileName } = file || {};

    const image = fileName || chapterData.image_url;

    return (
      <Fragment>
        <Modal
          id="chapter-create-modal"
          open={modalOpen}
          onClose={this.handleModalClose}
          size="small"
        >
          <Header icon="tasks" content="Create Chapter" />
          <Row justifyContent="center">
            {error && <Message error={true}>{error}</Message>}
          </Row>
          <Modal.Content style={{ pointerEvents: 'none' }}>
            <Form onChange={this.handleChange}>
              <Form.Group>
                <Form.Input
                  name={'name'}
                  label={'Name'}
                  width={5}
                  value={chapterData.name}
                />
                <Form.Input
                  name={'code'}
                  label={'Code'}
                  width={5}
                  value={chapterData.code}
                  disabled={isChapterNew ? false : true}
                />
                <Form.Input
                  name={'version'}
                  label={'version'}
                  width={2}
                  value={chapterData.version || -1}
                  disabled={true}
                />
                <Form.Input
                  name={'position'}
                  label={'Chapter Position'}
                  width={5}
                  value={chapterData.position}
                />
              </Form.Group>
              <Form.Group>
                <TextArea
                  name={'description'}
                  label={'Description'}
                  width={3}
                  placeholder="Chapter Description"
                  value={chapterData.description}
                />
              </Form.Group>
              <Form.Group>
                <Form.Field label={'Is Live?'} control="checkbox">
                  <Checkbox
                    name={'is_live'}
                    toggle
                    label="Yes"
                    checked={chapterData.is_live || false}
                    onChange={this.handleChangeCheckbox}
                  />
                </Form.Field>
                <Form.Field label={'Is Default?'} control="checkbox">
                  <Checkbox
                    name={'is_default'}
                    toggle
                    label="Yes"
                    checked={chapterData.is_default}
                    onChange={this.handleChangeCheckbox}
                  />
                </Form.Field>
                <Form.Field label={'Is Mastery Enabled?'} control="checkbox">
                  <Checkbox
                    name={'mastery_enabled'}
                    toggle
                    label="Yes"
                    checked={chapterData.mastery_enabled}
                    onChange={this.handleChangeCheckbox}
                  />
                </Form.Field>
                <Form.Field label={'Is Item Bank Enabled?'} control="checkbox">
                  <Checkbox
                    name={'item_bank_enabled'}
                    toggle
                    label="Yes"
                    checked={chapterData.item_bank_enabled}
                    onChange={this.handleChangeCheckbox}
                  />
                </Form.Field>

                <Form.Group>
                  <RowStyle>
                    <HeaderButton
                      label={image ? 'Edit Image' : 'Add Image'}
                      icon={FileUpload}
                      onClick={this.onButtonClick}
                    />

                    <input
                      type="file"
                      ref={this.inputFile}
                      id="file-upload"
                      style={{ display: 'none' }}
                      onChange={this.setImage}
                    />
                    <TextStyle maxLength="10">{image}</TextStyle>
                    {image && <DeleteIcon alt="delete" onClick={this.deleteImage} />}
                  </RowStyle>
                </Form.Group>
              </Form.Group>
            </Form>
            <Divider />
            {/* <Button.Group floated="right">
              <Button
                color="green"
                icon="check"
                onClick={this.handleSubmit}
                content={isChapterNew ? 'Add New Chapter' : 'Edit Chapter'}
              />
            </Button.Group>
            <Divider /> */}
          </Modal.Content>
        </Modal>
        <NotificationPortal
          openSavedNotificationPortal={openSavedNotificationPortal}
          notificationMessage={notificationMessage}
          handleCloseNotificationPortal={this.handleCloseNotificationPortal}
          isError={isNotificationError}
        />

        <Menu>
          <Menu.Item>
            <Dropdown
              placeholder="Select Chapter"
              selection
              search
              options={this.getChapterOptions()}
              onChange={this.changeChapterCodeDropdownContent}
            />
          </Menu.Item>
          {/* <Menu.Item>
            <Button
              attached="left"
              icon="plus"
              onClick={this.newChapter}
              disabled={!program ? true : false}
            />
          </Menu.Item> */}
        </Menu>
        <Menu>
          <Menu.Item>
            <Dropdown
              placeholder="Select Chapter Version"
              selection
              options={this.getChapterVersionOptions()}
              onChange={this.changeActiveDropdownContent}
            />
            <Button
              attached="right"
              icon="eye"
              onClick={this.viewChapter}
              disabled={!isChapter}
            />
          </Menu.Item>
          {/* <Menu.Item>
            <Button
              disabled={!activeChapterCode}
              icon="plus"
              content="New"
              onClick={this.props.onNewChapter}
            />
          </Menu.Item> */}
        </Menu>
        <Button.Group fluid>
          <Button disabled={!activeEntityID} color="green" onClick={this.sendChapterData}>
            <Icon name="checkmark" /> Load
          </Button>
        </Button.Group>
      </Fragment>
    );
  }
}

export default connect(
  ({ chapters, drafts }) => ({
    chapters: chapters,
    drafts: drafts,
  }),
  dispatch => ({
    actions: bindActionCreators(
      { getChapterList, postChapter, getDraftList, patchChapter },
      dispatch,
    ),
  }),
)(ChapterDropdown);
